import { Component, computed, ElementRef, inject, input, model, output, viewChild } from '@angular/core';
import { LucideCamera, LucideIcon } from '@lucide/angular';
import { PlatformService } from '../../../../services/platform.service';
import { ButtonComponent } from '../../../button/button.component';
import { InlineButtonComponent } from '../../../inline-button/inline-button.component';
import { InputLLMAction, InputLLMActionResult } from '../action.model';

@Component({
  selector: 'joii-input-llm-camera-action',
  templateUrl: './camera-action.html',
  styleUrl: './camera-action.css',
  imports: [ButtonComponent, InlineButtonComponent],
})
export class CameraActionComponent implements InputLLMAction<Blob> {
  private readonly platformService = inject(PlatformService);

  type = input.required<'circle-button' | 'icon-only'>();
  icon = input<LucideIcon>(LucideCamera);
  label = input<string>('Take photo');
  disabled = input<boolean>(false);
  /** Override platform detection: 'camera' forces capture mode, 'upload' forces file-picker mode. */
  mode = input<'camera' | 'upload'>();

  actionResult = output<InputLLMActionResult<Blob>>();

  private readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  readonly active = model<boolean>(false);

  readonly isMobile = this.platformService.isMobile;
  readonly inputAccept = computed(() => 'image/*');
  readonly captureAttribute = computed(() => {
    const m = this.mode();
    if (m === 'camera') return 'environment';
    if (m === 'upload') return null;
    return this.isMobile() ? 'environment' : null;
  });
  readonly ariaLabel = computed(() => {
    const m = this.mode();
    if (m === 'camera') return 'Take a photo';
    if (m === 'upload') return 'Upload an image';
    return this.isMobile() ? 'Take a photo' : 'Upload an image';
  });

  protected onButtonClick(): void {
    if (this.disabled()) return;
    this.fileInput().nativeElement.click();
  }

  protected onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    this.active.set(true);

    const blob: Blob = file;
    this.actionResult.emit({ kind: 'data', payload: blob });

    input.value = '';
    this.active.set(false);
  }
}
